import useCarers from '@features/organisation/hooks/useCarers'
import type { Carer, Home } from '@lmc/ts-common'
import useResidentsList from 'hooks/useResidentsList'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

export type StepOption = { disabled?: boolean; label: string; value: string }

/** A saved person as the read embeds them; null if they no longer exist. */
export type SavedPerson = { id: string; name?: unknown } | null

/**
 * The options plus anyone saved who is no longer among them, e.g. someone who
 * has left, so a saved pick shows their name rather than an id. With
 * `isLocked`, those added can't be picked again.
 */
export const withSavedPeople = (
  options: ReadonlyArray<StepOption>,
  saved: ReadonlyArray<SavedPerson> | null | undefined,
  { isLocked = false }: { isLocked?: boolean } = {}
): Array<StepOption> => [
  ...options,
  ...(saved ?? []).flatMap((person) =>
    person &&
    typeof person.name === 'string' &&
    !options.some((option) => option.value === person.id)
      ? [
          {
            label: person.name,
            value: person.id,
            ...(isLocked && { disabled: true })
          }
        ]
      : []
  )
]

/** Options from a code-to-locale-key map, in the map's order. */
export const keyOptions = <Code extends string>(
  keys: Record<Code, string>,
  t: (key: string) => string
) =>
  (Object.entries(keys) as Array<[Code, string]>).map(([value, key]) => ({
    label: t(key),
    value
  }))

/**
 * Active team members of the current home, as select options. `filter` limits
 * them, e.g. to those who can own a case; `saved` ids stay named even once
 * they no longer qualify. With a `filter`, those can't be picked again.
 */
export const useTeamMemberOptions = ({
  filter,
  saved = []
}: {
  filter?: (carer: Carer) => boolean
  saved?: ReadonlyArray<string | null | undefined>
} = {}): Array<StepOption> => {
  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )
  const { carers } = useCarers()
  const homeId = home?.id ?? ''
  const named = carers.filter(
    (carer) => carer.name.first !== undefined && carer.name.last !== undefined
  )
  const toOption = (carer: (typeof carers)[number]) => ({
    label: `${carer.name.first} ${carer.name.last}`,
    value: carer.id
  })
  const choosable = named
    .filter(
      (carer) =>
        carer.active &&
        (!filter || filter(carer)) &&
        (carer.home === homeId || carer.homes.includes(homeId))
    )
    .map(toOption)

  return withSavedPeople(
    choosable,
    named
      .filter((carer) => saved.includes(carer.id))
      .map((carer) => ({ id: carer.id, name: toOption(carer).label }))
  ).map((option, index) =>
    filter && index >= choosable.length ? { ...option, disabled: true } : option
  )
}

/**
 * The home's active service users other than `excludeId`, as select options;
 * discharged, deceased and other inactive service users can't be picked.
 * `includeInactive` adds them back, for looking up a saved person's name.
 */
export const useServiceUserOptions = ({
  excludeId,
  includeInactive = false
}: {
  excludeId?: string
  includeInactive?: boolean
} = {}): Array<StepOption> => {
  const { activeResidents, residents } = useResidentsList({})
  const serviceUsers = includeInactive ? residents : activeResidents

  return useMemo(
    () =>
      (serviceUsers ?? [])
        .filter(
          (resident) =>
            resident.name.first !== undefined &&
            resident.name.last !== undefined &&
            resident.id !== excludeId
        )
        .map((resident) => ({
          label: `${resident.name.first} ${resident.name.last}`,
          value: resident.id
        })),
    [serviceUsers, excludeId]
  )
}
